import { useCallback, useSyncExternalStore } from 'react';

/** Widest viewport (px) that gets the phone/tablet layout. */
export const MOBILE_BREAKPOINT = 840;

/** Media query matching `MOBILE_BREAKPOINT`; mirror it in CSS `@media` blocks. */
export const MOBILE_MEDIA_QUERY = `(max-width: ${MOBILE_BREAKPOINT}px)`;

function getMediaQueryList(query) {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return null;
    return window.matchMedia(query);
}

// Server render and jsdom without a `matchMedia` polyfill both resolve to the
// desktop layout so existing tests keep exercising the unchanged branch.
function getServerSnapshot() {
    return false;
}

/**
 * Subscribe to a CSS media query. Returns `false` when `window.matchMedia`
 * is unavailable (SSR, tests) and re-renders when the match flips.
 *
 * @param {string} query e.g. `(max-width: 840px)`
 * @returns {boolean}
 */
export function useMediaQuery(query) {
    const subscribe = useCallback(
        (onChange) => {
            const mql = getMediaQueryList(query);
            if (!mql) return () => {};
            if (typeof mql.addEventListener === 'function') {
                mql.addEventListener('change', onChange);
                return () => mql.removeEventListener('change', onChange);
            }
            // Safari < 14 only exposes the legacy listener API.
            if (typeof mql.addListener === 'function') {
                mql.addListener(onChange);
                return () => mql.removeListener(onChange);
            }
            return () => {};
        },
        [query],
    );
    const getSnapshot = useCallback(() => Boolean(getMediaQueryList(query)?.matches), [query]);
    return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

/** `true` at or below `MOBILE_BREAKPOINT`. */
export function useIsMobile() {
    return useMediaQuery(MOBILE_MEDIA_QUERY);
}
